import { useEffect, useState, useCallback, useMemo, type ReactNode, type FormEvent } from 'react';
import { Link, useParams } from 'react-router-dom';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { Button } from '../../../../shared/components/index.js';
import { useBusinessNumber } from '../../../../shared/hooks/useBusinessNumber.js';

/**
 * Dynamic budget preparation.
 *
 * Every dimension, member and period comes from configuration, so a dimension
 * added as data appears here with no code change. There is deliberately no
 * dimension name, geography level or month name anywhere in this file.
 *
 * The grid is a raw <table> rather than <DataTable> because its columns are
 * generated from the tenant's planning cycle and its cells are editable — the
 * dynamic-column/matrix case already carried on the DataTable allowlist.
 */

const CYCLE_KEY = 'CYCLE';
/** Beyond this many selected parents, fall back to search rather than one fetch each. */
const MAX_PARENT_FETCHES = 8;

interface Dimension {
  readonly budgetDimensionId: string;
  readonly dimensionCode: string;
  readonly dimensionName: string;
  readonly parentDimensionCode: string | null;
  readonly displayOrder: number;
}
interface PlanDimension {
  readonly dimensionCode: string;
  readonly displayOrder: number;
}
interface Member {
  readonly memberId: string;
  readonly memberCode: string;
  readonly memberName: string;
  readonly parentMemberId: string | null;
}
interface Period {
  readonly planningPeriodId: string;
  readonly periodCode: string;
}
interface MatrixMember {
  readonly dimensionCode: string;
  readonly memberId: string;
  readonly memberCode: string;
  readonly memberName: string;
}
interface MatrixCell {
  readonly budgetLineId: string;
  readonly amountInr: number;
}
interface MatrixRow {
  readonly dimensionSignature: string;
  readonly members: MatrixMember[];
  readonly cells: Record<string, MatrixCell>;
  readonly rowTotal: number;
}
interface Matrix {
  readonly planId: string;
  readonly periodGrain: 'CYCLE' | 'PERIOD';
  readonly planningCycleId: string | null;
  readonly dimensions: Array<{ dimensionCode: string; displayOrder: number }>;
  readonly rows: MatrixRow[];
  readonly periodTotals: Record<string, number>;
  readonly grandTotal: number;
}

/** A grid row: either persisted (has a signature) or staged in the browser. */
interface GridRow {
  readonly key: string;
  readonly members: MatrixMember[];
  readonly signature: string | null;
}

const CARD: React.CSSProperties = {
  padding: 16, borderRadius: 8, marginBottom: 16,
  border: '1px solid var(--color-border-default)',
  background: 'var(--color-bg-elevated)',
};
const INPUT: React.CSSProperties = {
  width: '100%', padding: 8, borderRadius: 4,
  border: '1px solid var(--color-border-default)',
};
const CELL: React.CSSProperties = {
  width: 110, padding: 4, borderRadius: 4, textAlign: 'right',
  border: '1px solid var(--color-border-default)',
};
const TH: React.CSSProperties = {
  padding: 8, textAlign: 'right', whiteSpace: 'nowrap',
  borderBottom: '1px solid var(--color-border-default)',
};
const TD: React.CSSProperties = {
  padding: 4, borderBottom: '1px solid var(--color-border-default)',
};


/** Stable local identity of a combination; the server owns the real signature. */
function rowKeyOf(members: MatrixMember[]): string {
  return [...members]
    .sort((a, b) => a.dimensionCode.localeCompare(b.dimensionCode))
    .map((m) => `${m.dimensionCode}=${m.memberId}`)
    .join('|');
}

export function ShBudgetPlanningPage(): ReactNode {
  const { planId = '' } = useParams<{ planId: string }>();
  const { token } = useAuth();
  const { t } = useTranslation();
  // Amounts follow the tenant's DISPLAY_NUMBER_FORMAT, not a locale in the source.
  const { number } = useBusinessNumber();

  const [planName, setPlanName] = useState('');
  const [matrix, setMatrix] = useState<Matrix | null>(null);
  const [dimensions, setDimensions] = useState<Dimension[]>([]);
  const [planDims, setPlanDims] = useState<PlanDimension[]>([]);
  const [periods, setPeriods] = useState<Period[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // grain declaration
  const [chosen, setChosen] = useState<string[]>([]);
  const [grain, setGrain] = useState<'CYCLE' | 'PERIOD'>('PERIOD');
  const [redeclaring, setRedeclaring] = useState(false);

  // combination builder
  const [options, setOptions] = useState<Record<string, Member[]>>({});
  const [picked, setPicked] = useState<Record<string, string[]>>({});
  const [search, setSearch] = useState<Record<string, string>>({});

  // staged rows and unsaved cell edits
  const [staged, setStaged] = useState<GridRow[]>([]);
  const [draft, setDraft] = useState<Record<string, Record<string, string>>>({});

  const load = useCallback(async () => {
    try {
      const [plan, dims, pd, mx] = await Promise.all([
        apiRequest<{ planName: string }>(`/v1/budget/plans/${planId}`, { token }),
        apiRequest<Dimension[]>('/v1/budget/dimensions', { token }),
        apiRequest<PlanDimension[]>(`/v1/budget/plans/${planId}/dimensions`, { token }),
        apiRequest<Matrix>(`/v1/budget/plans/${planId}/matrix`, { token }),
      ]);
      setPlanName(plan.planName);
      setDimensions(dims);
      setPlanDims(pd);
      setMatrix(mx);
      setGrain(mx.periodGrain);
      setError(null);
    } catch (e) { setError((e as Error).message); }
  }, [planId, token]);

  useEffect(() => { void load(); }, [load]);

  useEffect(() => {
    const cycleId = matrix?.planningCycleId;
    if (!cycleId) return;
    void (async () => {
      try {
        const r = await apiRequest<Period[]>(
          `/v1/m17/config/planning-cycles/${cycleId}/periods`, { token });
        setPeriods(Array.isArray(r) ? r : []);
      } catch { /* a year-wise plan needs no periods */ }
    })();
  }, [matrix?.planningCycleId, token]);

  const declaredCodes = useMemo(() => planDims.map((d) => d.dimensionCode), [planDims]);
  const dimByCode = useMemo(
    () => new Map(dimensions.map((d) => [d.dimensionCode, d])),
    [dimensions],
  );

  /**
   * Loads one dimension's members, narrowed to whichever parents are already
   * selected. This is what stops the Item picker offering another type's items
   * and the District picker listing every district in the country.
   */
  const loadMembers = useCallback(async (code: string) => {
    const dim = dimByCode.get(code);
    if (!dim) return;
    const term = search[code]?.trim() ?? '';
    const parentCode = dim.parentDimensionCode;
    const parents = parentCode ? (picked[parentCode] ?? []) : [];

    const url = (extra: string) =>
      `/v1/budget/dimensions/${encodeURIComponent(code)}/members?`
      + (term ? `search=${encodeURIComponent(term)}&` : '')
      + extra;

    try {
      let list: Member[];
      if (parentCode && parents.length > 0 && parents.length <= MAX_PARENT_FETCHES) {
        const batches = await Promise.all(
          parents.map((p) =>
            apiRequest<Member[]>(url(`parentMemberId=${encodeURIComponent(p)}`), { token })),
        );
        const seen = new Set<string>();
        list = batches.flat().filter((m) => (seen.has(m.memberId) ? false : seen.add(m.memberId)));
      } else {
        list = await apiRequest<Member[]>(url(''), { token });
      }
      setOptions((prev) => ({ ...prev, [code]: list }));
    } catch (e) { setError((e as Error).message); }
  }, [dimByCode, picked, search, token]);

  // Reload a dimension's options whenever the parent selection changes.
  useEffect(() => {
    for (const code of declaredCodes) void loadMembers(code);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [declaredCodes, JSON.stringify(picked)]);

  const columns = useMemo(() => (
    grain === 'PERIOD'
      ? periods.map((p) => ({ key: p.planningPeriodId, label: p.periodCode }))
      : [{ key: CYCLE_KEY, label: t('sh.budgetPlanning.yearWise') }]
  ), [grain, periods, t]);

  const rows: GridRow[] = useMemo(() => {
    const persisted: GridRow[] = (matrix?.rows ?? []).map((r) => ({
      key: r.dimensionSignature,
      members: r.members,
      signature: r.dimensionSignature,
    }));
    const known = new Set(persisted.map((r) => rowKeyOf(r.members)));
    return [...persisted, ...staged.filter((s) => !known.has(rowKeyOf(s.members)))];
  }, [matrix, staged]);

  const cellValue = useCallback((row: GridRow, colKey: string): string => {
    const edited = draft[row.key]?.[colKey];
    if (edited !== undefined) return edited;
    const saved = row.signature
      ? matrix?.rows.find((r) => r.dimensionSignature === row.signature)?.cells[colKey]
      : undefined;
    return saved ? String(saved.amountInr) : '';
  }, [draft, matrix]);

  // Live preview while typing. Saved figures come back from the server, which
  // stays the system of record for every total (R8).
  const rowTotal = useCallback((row: GridRow): number =>
    columns.reduce((sum, c) => sum + (Number(cellValue(row, c.key)) || 0), 0),
    [columns, cellValue]);

  const colTotal = useCallback((colKey: string): number =>
    rows.reduce((sum, r) => sum + (Number(cellValue(r, colKey)) || 0), 0),
    [rows, cellValue]);

  const grandTotal = useMemo(
    () => rows.reduce((sum, r) => sum + rowTotal(r), 0),
    [rows, rowTotal],
  );

  const dirty = Object.keys(draft).length > 0;

  const declare = useCallback(async (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true); setError(null); setInfo(null);
    try {
      await apiRequest(`/v1/budget/plans/${planId}/dimensions`, {
        method: 'POST', token,
        body: { dimensionCodes: chosen, periodGrain: grain },
      });
      setInfo(t('sh.budgetPlanning.grainSaved'));
      setRedeclaring(false);
      setStaged([]); setDraft({});
      await load();
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  }, [planId, chosen, grain, token, busy, load, t]);

  const addCombinations = useCallback(() => {
    setError(null); setInfo(null);
    if (declaredCodes.some((c) => (picked[c] ?? []).length === 0)) {
      setError(t('sh.budgetPlanning.selectAtLeastOne'));
      return;
    }
    // cartesian product of the selected members — one grid row per combination
    let combos: MatrixMember[][] = [[]];
    for (const code of declaredCodes) {
      const chosenMembers = (options[code] ?? [])
        .filter((m) => picked[code].includes(m.memberId));
      combos = combos.flatMap((c) => chosenMembers.map((m) => [...c, {
        dimensionCode: code,
        memberId: m.memberId,
        memberCode: m.memberCode,
        memberName: m.memberName,
      }]));
    }
    setStaged((prev) => {
      const seen = new Set(prev.map((r) => rowKeyOf(r.members)));
      const next = combos
        .filter((members) => !seen.has(rowKeyOf(members)))
        .map((members) => ({ key: rowKeyOf(members), members, signature: null }));
      return [...prev, ...next];
    });
    setPicked({});
  }, [declaredCodes, picked, options, t]);

  const save = useCallback(async () => {
    if (busy || !dirty) return;
    setBusy(true); setError(null); setInfo(null);
    try {
      const cells = [];
      for (const row of rows) {
        const edits = draft[row.key];
        if (!edits) continue;
        for (const [colKey, raw] of Object.entries(edits)) {
          cells.push({
            dimensions: row.members.map((m) => ({
              dimensionCode: m.dimensionCode,
              memberId: m.memberId,
            })),
            planningPeriodId: colKey === CYCLE_KEY ? null : colKey,
            amountInr: raw.trim() === '' ? null : Number(raw),
          });
        }
      }
      await apiRequest(`/v1/budget/plans/${planId}/lines/bulk`, {
        method: 'POST', token, body: { cells },
      });
      setDraft({}); setStaged([]);
      setInfo(t('sh.budgetPlanning.saved'));
      await load();
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  }, [busy, dirty, rows, draft, planId, token, load, t]);

  const removeRow = useCallback(async (row: GridRow) => {
    if (!row.signature) {
      setStaged((prev) => prev.filter((r) => r.key !== row.key));
      setDraft((prev) => { const n = { ...prev }; delete n[row.key]; return n; });
      return;
    }
    setBusy(true); setError(null);
    try {
      await apiRequest(
        `/v1/budget/plans/${planId}/lines?signature=${encodeURIComponent(row.signature)}`,
        { method: 'DELETE', token });
      setDraft((prev) => { const n = { ...prev }; delete n[row.key]; return n; });
      await load();
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  }, [planId, token, load]);

  const declared = planDims.length > 0 && !redeclaring;

  return (
    <main aria-label="sh-budget-planning">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h1 style={{ fontSize: 20 }}>
          {t('sh.budgetPlanning.title')}{planName ? ` — ${planName}` : ''}
        </h1>
        <Link to={`/sh/budget/${planId}`} data-testid="back-detail">{t('sh.budgetPlanning.backToPlan')}</Link>
      </div>

      {error && <p role="alert" style={{ color: 'var(--color-danger)', marginBottom: 12 }}>{error}</p>}
      {info && <p role="status" style={{ color: 'var(--color-success)', marginBottom: 12 }}>{info}</p>}

      <section style={CARD} aria-label="grain-form">
        <h2 style={{ fontSize: 16, marginBottom: 4 }}>{t('sh.budgetPlanning.grainHeading')}</h2>
        <p style={{ fontSize: 12, marginBottom: 12, opacity: 0.8 }}>{t('sh.budgetPlanning.grainHelp')}</p>

        {declared ? (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
            <p data-testid="declared-grain">
              <strong>
                {planDims
                  .map((d) => dimByCode.get(d.dimensionCode)?.dimensionName ?? d.dimensionCode)
                  .join(' × ')}
              </strong>
              {' · '}
              {grain === 'PERIOD' ? t('sh.budgetPlanning.monthWise') : t('sh.budgetPlanning.yearWise')}
            </p>
            <Button
              type="button"
              data-testid="btn-change-grain"
              onClick={() => { setChosen(declaredCodes); setRedeclaring(true); }}
            >
              {t('sh.budgetPlanning.changeGrain')}
            </Button>
          </div>
        ) : (
          <form onSubmit={declare} aria-label="declare-grain-form">
            {planDims.length > 0 && (
              <p role="alert" style={{ fontSize: 12, marginBottom: 12, color: 'var(--color-danger)' }}>
                {t('sh.budgetPlanning.changeGrainWarning')}
              </p>
            )}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 8, marginBottom: 12 }}>
              {dimensions.map((d) => (
                <label key={d.budgetDimensionId} style={{ fontSize: 13 }}>
                  <input
                    type="checkbox"
                    checked={chosen.includes(d.dimensionCode)}
                    data-testid={`dim-${d.dimensionCode}`}
                    onChange={(e) => setChosen((prev) => e.target.checked
                      ? [...prev, d.dimensionCode]
                      : prev.filter((c) => c !== d.dimensionCode))}
                  />{' '}{d.dimensionName}
                </label>
              ))}
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 13, marginRight: 16 }}>
                <input type="radio" name="grain" checked={grain === 'PERIOD'}
                       data-testid="grain-period"
                       onChange={() => setGrain('PERIOD')} />{' '}{t('sh.budgetPlanning.monthWise')}
              </label>
              <label style={{ fontSize: 13 }}>
                <input type="radio" name="grain" checked={grain === 'CYCLE'}
                       data-testid="grain-cycle"
                       onChange={() => setGrain('CYCLE')} />{' '}{t('sh.budgetPlanning.yearWise')}
              </label>
            </div>
            <Button type="submit" disabled={busy || chosen.length === 0} data-testid="btn-declare">
              {t('sh.budgetPlanning.saveGrain')}
            </Button>
          </form>
        )}
      </section>

      {declared && (
        <section style={CARD} aria-label="combination-builder">
          <h2 style={{ fontSize: 16, marginBottom: 4 }}>{t('sh.budgetPlanning.builderHeading')}</h2>
          <p style={{ fontSize: 12, marginBottom: 12, opacity: 0.8 }}>{t('sh.budgetPlanning.builderHelp')}</p>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 12 }}>
            {planDims.map((pd) => {
              const dim = dimByCode.get(pd.dimensionCode);
              const list = options[pd.dimensionCode] ?? [];
              const label = dim?.dimensionName ?? pd.dimensionCode;
              return (
                <div key={pd.dimensionCode}>
                  <label htmlFor={`search-${pd.dimensionCode}`} style={{ fontSize: 12, fontWeight: 600 }}>
                    {label}
                  </label>
                  <input
                    id={`search-${pd.dimensionCode}`}
                    style={{ ...INPUT, marginBottom: 4 }}
                    placeholder={t('sh.budgetPlanning.searchPlaceholder')}
                    data-testid={`search-${pd.dimensionCode}`}
                    value={search[pd.dimensionCode] ?? ''}
                    onChange={(e) => setSearch((s) => ({ ...s, [pd.dimensionCode]: e.target.value }))}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') { e.preventDefault(); void loadMembers(pd.dimensionCode); }
                    }}
                  />
                  <select
                    multiple
                    size={6}
                    style={{ ...INPUT, height: 'auto' }}
                    aria-label={label}
                    data-testid={`select-${pd.dimensionCode}`}
                    value={picked[pd.dimensionCode] ?? []}
                    onChange={(e) => setPicked((p) => ({
                      ...p,
                      [pd.dimensionCode]: Array.from(e.target.selectedOptions, (o) => o.value),
                    }))}
                  >
                    {list.map((m) => (
                      <option key={m.memberId} value={m.memberId}>
                        {m.memberName} ({m.memberCode})
                      </option>
                    ))}
                  </select>
                </div>
              );
            })}
          </div>

          <div style={{ marginTop: 12 }}>
            <Button type="button" onClick={addCombinations} data-testid="btn-add-combinations">
              {t('sh.budgetPlanning.addCombinations')}
            </Button>
          </div>
        </section>
      )}

      {declared && (
        <section style={CARD} aria-label="budget-grid">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <h2 style={{ fontSize: 16 }}>{t('sh.budgetPlanning.gridHeading')}</h2>
            <Button type="button" onClick={() => void save()} disabled={busy || !dirty}
                    data-testid="btn-save-grid">
              {busy ? t('sh.budgetPlanning.saving') : t('sh.budgetPlanning.save')}
            </Button>
          </div>

          {rows.length === 0 ? (
            <p style={{ color: 'var(--color-text-muted)' }}>{t('sh.budgetPlanning.noCombinations')}</p>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 13 }}
                     aria-label="budget-matrix">
                <thead>
                  <tr>
                    <th scope="col" style={{ ...TH, textAlign: 'left' }}>
                      {t('sh.budgetPlanning.combination')}
                    </th>
                    {columns.map((c) => (
                      <th scope="col" key={c.key} style={TH}>{c.label}</th>
                    ))}
                    <th scope="col" style={TH}>{t('sh.budgetPlanning.total')}</th>
                    <th scope="col" style={TH}>{t('sh.budgetPlanning.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.key}>
                      <th scope="row" style={{ ...TD, textAlign: 'left', fontWeight: 400 }}>
                        {row.members
                          .slice()
                          .sort((a, b) => a.dimensionCode.localeCompare(b.dimensionCode))
                          .map((m) => m.memberName)
                          .join(' / ')}
                        {row.signature === null && (
                          <em style={{ fontSize: 11, opacity: 0.7 }}>
                            {' '}— {t('sh.budgetPlanning.unsaved')}
                          </em>
                        )}
                      </th>
                      {columns.map((c) => (
                        <td key={c.key} style={TD}>
                          <input
                            type="number" min="0" step="0.01" style={CELL}
                            aria-label={`${row.key} ${c.label}`}
                            data-testid={`cell-${row.key}-${c.key}`}
                            value={cellValue(row, c.key)}
                            onChange={(e) => setDraft((d) => ({
                              ...d,
                              [row.key]: { ...(d[row.key] ?? {}), [c.key]: e.target.value },
                            }))}
                          />
                        </td>
                      ))}
                      <td style={{ ...TD, textAlign: 'right', fontWeight: 600 }}
                          data-testid={`rowtotal-${row.key}`}>
                        {number(rowTotal(row))}
                      </td>
                      <td style={{ ...TD, textAlign: 'right' }}>
                        <Button type="button" data-testid={`remove-${row.key}`}
                                onClick={() => void removeRow(row)}>
                          {t('sh.budgetPlanning.removeRow')}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <th scope="row" style={{ ...TD, textAlign: 'left' }}>
                      {t('sh.budgetPlanning.columnTotal')}
                    </th>
                    {columns.map((c) => (
                      <td key={c.key} style={{ ...TD, textAlign: 'right', fontWeight: 600 }}
                          data-testid={`coltotal-${c.key}`}>
                        {number(colTotal(c.key))}
                      </td>
                    ))}
                    <td style={{ ...TD, textAlign: 'right', fontWeight: 700 }} data-testid="grand-total">
                      {number(grandTotal)}
                    </td>
                    <td style={TD} />
                  </tr>
                </tfoot>
              </table>
            </div>
          )}

          {dirty && (
            <p style={{ fontSize: 12, marginTop: 8, opacity: 0.8 }} data-testid="unsaved-note">
              {t('sh.budgetPlanning.unsavedPreview')}
            </p>
          )}
          {matrix && !dirty && (
            <p style={{ fontSize: 12, marginTop: 8, opacity: 0.8 }} data-testid="server-total">
              {t('sh.budgetPlanning.grandTotal')}: ₹{number(matrix.grandTotal)}
            </p>
          )}
        </section>
      )}
    </main>
  );
}
